import React, { useEffect, useState } from 'react';
import { getSupportedEncounterNumberTemplateTokens, validateEncounterNumberTemplate } from 'common/lib/util/encounterNumberTemplate';
import SideModal from 'src/components/SideModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import Input, { Label } from 'src/components/Input';
import Flex from 'src/components/Flex';
import AiPromptDropdown from 'src/components/AiPromptDropdown';
import { useCreatePromptContext, useUpdatePromptContext, AiPromptContext } from 'src/queries/useAiPromptContexts';
import { usePrompts, AiPrompt } from 'src/queries/useAiPrompts';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import styles from './styles.module.scss';

interface EditAiPromptContextModalProps {
    isOpen: boolean;
    onClose: () => void;
    promptContext?: AiPromptContext | null;
    mode: 'create' | 'edit';
    facilityId?: number;
}

const OUTPUT_SCHEMA_ROOT_TYPE = 'object';
const ENCOUNTER_TEMPLATE_HELP_TOKENS = getSupportedEncounterNumberTemplateTokens().map((token) => `{{${token}}}`);

type FormState = {
    aiPromptId: string;
    facilityId?: number;
    aiPromptContextText: string;
    outputSchemaText: string;
    encounterNumberTemplate: string;
};

function formatOutputSchema(outputSchema?: object): string {
    if (!outputSchema) {
        return '';
    }

    try {
        return JSON.stringify(outputSchema, null, 2);
    } catch {
        return '';
    }
}

function parseOutputSchema(outputSchemaText: string): { outputSchema: object | null; error?: string } {
    const trimmedSchema = outputSchemaText.trim();
    if (!trimmedSchema) {
        return { outputSchema: null };
    }

    let parsedSchema: unknown;
    try {
        parsedSchema = JSON.parse(trimmedSchema);
    } catch {
        return { outputSchema: null, error: 'Output Schema must be valid JSON' };
    }

    if (!parsedSchema || typeof parsedSchema !== 'object' || Array.isArray(parsedSchema)) {
        return { outputSchema: null, error: 'Output Schema must be a JSON object' };
    }

    const schema = parsedSchema as { type?: unknown; properties?: unknown };
    if (schema.type !== OUTPUT_SCHEMA_ROOT_TYPE) {
        return {
            outputSchema: null,
            error: 'Output Schema must include root "type": "object"',
        };
    }

    if (!schema.properties || typeof schema.properties !== 'object' || Array.isArray(schema.properties)) {
        return {
            outputSchema: null,
            error: 'Output Schema must include a root "properties" object',
        };
    }

    return { outputSchema: parsedSchema as object };
}

function parseEncounterNumberTemplateError(encounterNumberTemplate: string): string | null {
    const trimmedTemplate = encounterNumberTemplate.trim();
    if (!trimmedTemplate) {
        return null;
    }

    try {
        validateEncounterNumberTemplate(trimmedTemplate);
        return null;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('unsupported token')) {
            return 'Encounter Number Template contains unsupported token(s). Use "Available tokens" to view supported values.';
        }
        if (errorMessage.includes('malformed token braces')) {
            return 'Encounter Number Template has malformed token braces. Use {{TOKEN_NAME}} syntax.';
        }
        if (errorMessage.includes('malformed token syntax')) {
            return 'Encounter Number Template has invalid token syntax. Use uppercase letters, numbers, and underscores.';
        }

        return 'Encounter Number Template is invalid';
    }
}

function EditAiPromptContextModal({ isOpen, onClose, promptContext, mode, facilityId }: EditAiPromptContextModalProps) {
    const createPromptContextMutation = useCreatePromptContext();
    const updatePromptContextMutation = useUpdatePromptContext();
    const { data: prompts = [] } = usePrompts();

    const [promptContextData, setPromptContextData] = useState<FormState>({
        aiPromptId: '',
        facilityId,
        aiPromptContextText: '',
        outputSchemaText: '',
        encounterNumberTemplate: '',
    });
    const [selectedPrompt, setSelectedPrompt] = useState<AiPrompt | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const isLoading = createPromptContextMutation.isLoading || updatePromptContextMutation.isLoading;

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && promptContext) {
                const { aiPromptId, facilityId, aiPromptContextText, outputSchema, encounterNumberTemplate } =
                    promptContext;
                const prompt = prompts.find((p: AiPrompt) => p.promptId === aiPromptId);
                setPromptContextData({
                    aiPromptId,
                    facilityId,
                    aiPromptContextText: aiPromptContextText || '',
                    outputSchemaText: formatOutputSchema(outputSchema),
                    encounterNumberTemplate: encounterNumberTemplate || '',
                });
                setSelectedPrompt(prompt || null);
            } else {
                setPromptContextData({
                    aiPromptId: '',
                    facilityId,
                    aiPromptContextText: '',
                    outputSchemaText: '',
                    encounterNumberTemplate: '',
                });
                setSelectedPrompt(null);
            }
            setErrors({});
        }
    }, [isOpen, mode, promptContext, facilityId, prompts]);

    const validateForm = (): { isValid: boolean; outputSchema: object | null } => {
        const newErrors: { [key: string]: string } = {};
        const outputSchemaResult = parseOutputSchema(promptContextData.outputSchemaText);

        if (!promptContextData.facilityId || promptContextData.facilityId === 0) {
            newErrors.facilityId = 'No facility selected. Please select a facility first.';
        }

        if (!promptContextData.aiPromptId) {
            newErrors.aiPromptId = 'AI Prompt is required';
        }

        if (!promptContextData.aiPromptContextText.trim()) {
            newErrors.aiPromptContextText = 'Context text is required';
        }

        if (outputSchemaResult.error) {
            newErrors.outputSchemaText = outputSchemaResult.error;
        }

        if (promptContextData.encounterNumberTemplate.length > 1000) {
            newErrors.encounterNumberTemplate = 'Encounter Number Template must be 1000 characters or less';
        } else {
            const encounterNumberTemplateError = parseEncounterNumberTemplateError(promptContextData.encounterNumberTemplate);
            if (encounterNumberTemplateError) {
                newErrors.encounterNumberTemplate = encounterNumberTemplateError;
            }
        }

        setErrors(newErrors);
        return {
            isValid: Object.keys(newErrors).length === 0,
            outputSchema: outputSchemaResult.outputSchema,
        };
    };

    const handleSubmit = async () => {
        const validation = validateForm();
        if (!validation.isValid) {
            return;
        }

        if (mode === 'edit' && !promptContext) {
            ToastStoreObject.show('Error: No prompt context selected for editing', ToastType.Error);
            return;
        }

        try {
            const promptContextPayload: Partial<AiPromptContext> = {
                aiPromptId: promptContextData.aiPromptId,
                facilityId: promptContextData.facilityId,
                aiPromptContextText: promptContextData.aiPromptContextText,
                outputSchema: validation.outputSchema,
                encounterNumberTemplate: promptContextData.encounterNumberTemplate.trim() || null,
            };

            if (mode === 'create') {
                await createPromptContextMutation.mutateAsync(promptContextPayload);
                ToastStoreObject.show('Prompt context created successfully', ToastType.Success);
            } else {
                await updatePromptContextMutation.mutateAsync({
                    promptContextId: promptContext!.aiPromptContextId,
                    promptContextData: promptContextPayload,
                });
                ToastStoreObject.show('Prompt context updated successfully', ToastType.Success);
            }
            onClose();
        } catch (error) {
            const errorMessage = parseError(error);
            ToastStoreObject.show(errorMessage, ToastType.Error);
        }
    };

    const handleInputChange = (
        field: 'aiPromptContextText' | 'outputSchemaText' | 'encounterNumberTemplate',
        value: string,
    ) => {
        setPromptContextData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handlePromptChange = (prompt: AiPrompt | null) => {
        setSelectedPrompt(prompt);
        setPromptContextData((prev) => ({
            ...prev,
            aiPromptId: prompt?.promptId || '',
        }));
        if (errors.aiPromptId) {
            setErrors((prev) => ({ ...prev, aiPromptId: '' }));
        }
    };

    const getPromptName = (promptId: string) => {
        const prompt = prompts.find((p: AiPrompt) => p.promptId === promptId);
        return prompt ? prompt.promptName : 'Unknown Prompt';
    };

    return (
        <SideModal isOpen={isOpen} onClose={onClose}>
            <ModalHeader
                title={mode === 'create' ? 'Create Prompt Context' : 'Edit Prompt Context'}
                onClose={onClose}
            />

            <div className={styles.modalContent}>
                <Flex direction="column" className={styles.formContainer}>
                    {mode === 'edit' && (
                        <Input
                            label="Prompt Name"
                            value={getPromptName(promptContextData.aiPromptId)}
                            disabled
                            placeholder="Prompt name"
                        />
                    )}

                    {mode === 'create' && (
                        <div>
                            <Label text="AI Prompt" required />
                            <AiPromptDropdown
                                value={selectedPrompt}
                                onChange={handlePromptChange}
                                placeholder="Select a prompt"
                                isClearable={false}
                                infoState={errors.aiPromptId ? 'error' : undefined}
                            />
                        </div>
                    )}
                    <Input
                        label="Context Text"
                        value={promptContextData.aiPromptContextText}
                        onChangeText={(value: string) => handleInputChange('aiPromptContextText', value)}
                        placeholder="Enter the context text for this prompt"
                        multiLine
                        rows={25}
                        errorMessage={errors.aiPromptContextText}
                        required
                    />

                    <Input
                        label="Output Schema"
                        value={promptContextData.outputSchemaText}
                        onChangeText={(value: string) => handleInputChange('outputSchemaText', value)}
                        placeholder='Paste JSON schema (example: {"type":"object","properties":{...}})'
                        multiLine
                        rows={10}
                        errorMessage={errors.outputSchemaText}
                    />

                    <div className={styles.encounterTemplateSection}>
                        <Input
                            label="Encounter Number Template"
                            value={promptContextData.encounterNumberTemplate}
                            onChangeText={(value: string) => handleInputChange('encounterNumberTemplate', value)}
                            placeholder="Optional: EN{{FIRST_NAME_INITIAL}}{{LAST_NAME_INITIAL}}_{{ADMIT_DATE_YYYYMMDD}}{{ADMIT_TIME_HHMMSS}}"
                            maxLength={1000}
                            errorMessage={errors.encounterNumberTemplate}
                        />
                        <div className={styles.helperRow}>
                            <p className={styles.helperText}>
                                Format: plain text + <code>{'{{TOKEN_NAME}}'}</code> tokens.
                            </p>
                        </div>
                        <div className={styles.tokenList} data-test-id="encounter-template-token-list">
                            <span className={styles.tokenListLabel}>Available tokens:</span>{' '}
                            {ENCOUNTER_TEMPLATE_HELP_TOKENS.map((token, index) => (
                                <React.Fragment key={token}>
                                    <code>{token}</code>
                                    {index < ENCOUNTER_TEMPLATE_HELP_TOKENS.length - 1 ? ', ' : null}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </Flex>
            </div>

            <ModalFooter
                primaryText={mode === 'create' ? 'Create' : 'Update'}
                secondaryText="Cancel"
                primaryClick={handleSubmit}
                secondaryClick={onClose}
                isLoading={isLoading}
            />
        </SideModal>
    );
}

export default EditAiPromptContextModal;
